import {
  createFrontendModule,
  createRouteRef,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import type { NavContentComponentProps } from '@backstage/plugin-app-react';
import {
  Sidebar,
  SidebarItem,
  SidebarDivider,
  SidebarSpace,
  Link,
  useSidebarOpenState,
} from '@backstage/core-components';
import { usePermission } from '@backstage/plugin-permission-react';
import { createPermission } from '@backstage/plugin-permission-common';
import PeopleIcon from '@material-ui/icons/People';
import SearchIcon from '@material-ui/icons/Search';
import { UsersPage } from './UsersPage';
import { LogoFull } from '../nav/LogoFull';
import { LogoIcon } from '../nav/LogoIcon';
import { teamsRouteRef, teamsReadPermission } from '../teams';
import { domainsRouteRef } from '../domains';
import { imagesRouteRef } from '../images';
import { pipelinesRouteRef, pipelinesReadPermission } from '../pipelines';
import { appsRouteRef } from '../apps';

// routeRef explícito é obrigatório pra aparecer no menu lateral - sem ele a
// rota funciona normalmente (path sozinho já registra), mas o AppNav do
// Backstage (@backstage/plugin-app, extensions/AppNav.esm.js) descarta a
// página da lista de itens de navegação se ela não tiver um routeRef (usa
// isso, não o path, pra resolver o link do item de menu).
const usersRouteRef = createRouteRef();

const usersPage = PageBlueprint.make({
  name: 'users',
  params: {
    path: '/users',
    title: 'Usuários',
    icon: <PeopleIcon />,
    routeRef: usersRouteRef,
    loader: async () => <UsersPage />,
  },
});

// Mesmo nome do permission object do backend (packages/backend/src/modules/
// permissionPolicy.ts) - sem pacote compartilhado entre app/backend aqui,
// mas o isPermission() do Backstage compara só o "name", então dois objetos
// criados separadamente com o mesmo nome são equivalentes na prática.
const usersReadPermission = createPermission({
  name: 'keycloak-users.read',
  attributes: { action: 'read' },
});

// Logo clicável no topo do menu, trocando entre a versão cheia (menu aberto)
// e só o ícone (menu fechado/hover) - mesmo padrão do Root.tsx clássico do
// Backstage (@backstage/core-components, useSidebarOpenState). O
// NavLogoBlueprint que existia antes pra isso foi removido (ver CHANGELOG de
// @backstage/plugin-app) e dobrado dentro do NavContentBlueprint - agora é a
// própria nav-content que desenha a logo, não tem mais extensão separada.
function SidebarLogo() {
  const { isOpen } = useSidebarOpenState();
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: isOpen ? 'flex-start' : 'center',
        alignItems: 'center',
        height: 56,
        padding: '0 16px',
      }}
    >
      {/* "/" não é uma rota de verdade nesse app - a página Home fica em
          "/home" (@backstage/plugin-home), confirmado lendo o path real
          dentro do node_modules do próprio pacote. Link errado aqui batia
          direto no 404. */}
      <Link to="/home" underline="none" aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
}

// Rotas que viram itens fixos no rodapé do menu (padrão do Root.tsx clássico
// do Backstage: configurações/notificações separadas do menu principal por
// um SidebarSpace, que empurra tudo abaixo dele pro fim da tela).
const BOTTOM_HREFS = ['/settings', '/notifications'];
const SEARCH_HREF = '/search';
// "Ferramentas" - páginas de introspecção/debug do próprio Backstage, que no
// demo oficial (demo.backstage.io) ficam separadas do menu principal por um
// divisor (lá são Tech Radar/MUI to BUI/Visualizer; MUI to BUI é uma
// ferramenta interna do time do Backstage, não instalamos).
const TOOL_HREFS = ['/catalog-graph', '/catalog-import', '/visualizer', '/tech-radar'];
// Ordem curada do grupo principal (igual ao Root.tsx clássico do Backstage e
// ao demo.backstage.io: Home, Catalog, APIs, Docs, Create, Explore) - não é
// alfabética de propósito. Itens que não estão nessa lista (ex: "Usuários",
// que é nosso) vão depois, na ordem alfabética que já vem do sort acima.
const MENU_ORDER = ['Home', 'Catalog', 'APIs', 'Docs', 'Create', 'Explore'];

// Substitui o menu lateral inteiro (é o jeito do Backstage de customizar a
// nav - não existe um "esconder este item" pontual) só pra tirar "Usuários"
// de quem não é admin, e pra montar a estrutura de menu "de verdade" do
// Backstage (logo, atalho de busca, grupo principal, ferramentas, config
// fixada embaixo - mesmo padrão do demo.backstage.io) em vez da lista plana
// que o DefaultNavContent original (@backstage/plugin-app, AppNav.esm.js)
// desenha.
//
// Dois detalhes que já erraram aqui e valem o registro:
// 1) `navItems.rest()` (o `NavItemBag` de verdade, confirmado lendo o bundle
//    compilado de AppNav.esm.js) NÃO aceita nenhuma opção - passar
//    `{ sortBy: 'title' }` é só ignorado em silêncio (o typing público de
//    `NavContentComponentProps` documenta um `rest(options)` que devolve
//    JSX.Element[] e aceita sortBy, mas isso não bate com o objeto que
//    realmente chega aqui - por segurança, ordenamos nós mesmos com
//    `.sort()` depois de já termos o array em mãos).
// 2) `SidebarGroup` (@backstage/core-components) só tem efeito em telas
//    pequenas (vira um item na bottom navigation) - "on bigger screens, this
//    won't have any effect at the moment" (doc do próprio componente). Usar
//    `<SidebarGroup to="..." />` sem filhos pra fazer as vezes de um item de
//    menu clicável (como o "Search") simplesmente não renderiza nada em tela
//    grande. Por isso Search e os labels de grupo ("Menu"/"Settings") somem
//    - a estrutura de grupos do Backstage é só semântica/mobile, o
//    agrupamento visual em telas grandes é feito à mão com SidebarDivider,
//    exatamente como abaixo.
//
// Enquanto a permissão ainda está carregando, os itens administrativos
// "Usuários"/"Times"/"Pipelines"/"Aplicações"
// ficam escondidos (nunca aparecem e somem depois) - evita revelar a
// existência da página pra quem não tem acesso.
function FilteredNavContent({ navItems }: NavContentComponentProps) {
  const usersAccess = usePermission({ permission: usersReadPermission });
  const teamsAccess = usePermission({ permission: teamsReadPermission });
  const pipelinesAccess = usePermission({ permission: pipelinesReadPermission });
  const items = [...navItems.rest()]
    .sort((a, b) => a.title.localeCompare(b.title))
    .filter(
      item =>
        (item.routeRef !== usersRouteRef ||
          (!usersAccess.loading && usersAccess.allowed)) &&
        (item.routeRef !== teamsRouteRef ||
          (!teamsAccess.loading && teamsAccess.allowed)) &&
        (item.routeRef !== pipelinesRouteRef ||
          (!pipelinesAccess.loading && pipelinesAccess.allowed)) &&
        // "Aplicações" (ligar/desligar), "Domínios" e "Imagens" usam a mesma
        // permissão de "Pipelines" (admin).
        ((item.routeRef !== appsRouteRef &&
          item.routeRef !== domainsRouteRef &&
          item.routeRef !== imagesRouteRef) ||
          (!pipelinesAccess.loading && pipelinesAccess.allowed)),
    );

  const searchItem = items.find(item => item.href === SEARCH_HREF);
  const bottomItems = items.filter(item => BOTTOM_HREFS.includes(item.href));
  const toolItems = items.filter(item => TOOL_HREFS.includes(item.href));
  const menuItems = items
    .filter(
      item =>
        item.href !== SEARCH_HREF &&
        !BOTTOM_HREFS.includes(item.href) &&
        !TOOL_HREFS.includes(item.href),
    )
    .sort((a, b) => {
      const rank = (title: string) => {
        const index = MENU_ORDER.indexOf(title);
        return index === -1 ? MENU_ORDER.length : index;
      };
      return rank(a.title) - rank(b.title);
    });

  return (
    <Sidebar>
      <SidebarLogo />
      {searchItem && (
        <SidebarItem to={searchItem.href} icon={() => <SearchIcon />} text="Search" />
      )}
      <SidebarDivider />
      {menuItems.map(item => (
        <SidebarItem key={item.href} to={item.href} icon={() => item.icon} text={item.title} />
      ))}
      {toolItems.length > 0 && <SidebarDivider />}
      {toolItems.map(item => (
        <SidebarItem key={item.href} to={item.href} icon={() => item.icon} text={item.title} />
      ))}
      <SidebarSpace />
      <SidebarDivider />
      {bottomItems.map(item => (
        <SidebarItem key={item.href} to={item.href} icon={() => item.icon} text={item.title} />
      ))}
    </Sidebar>
  );
}

const usersNavContent = NavContentBlueprint.make({
  params: { component: FilteredNavContent },
});

export const usersModule = createFrontendModule({
  pluginId: 'app',
  extensions: [usersPage, usersNavContent],
});
